"use client";
import { motion } from "framer-motion";
import React, { useState } from "react";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

const OurStory: React.FC = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <section
      className="relative w-full overflow-hidden py-24 shadow-lg transition-all duration-300"
      style={{
        backgroundColor: colors.white,
        boxShadow: `0px 0px 40px ${colors.primary}20`,
      }}
    >
      <div 
        className="absolute inset-0 opacity-50 blur-lg -z-10"
        style={{ background: `linear-gradient(to bottom, ${colors.primary}20, transparent)` }}
      />

      <div className="container mx-auto px-6 lg:px-12 max-w-6xl relative z-10">
        <motion.h2
          className="mb-16 text-center text-4xl font-bold sm:text-5xl"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{ color: colors.primary }}
        >
          <span className="relative inline-block">
            <span className="bg-gradient-to-r bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}>
              Our Evolution
            </span>
            <span className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}></span>
          </span>
        </motion.h2>

        <div className="relative">
          <div 
            className="absolute left-1/2 h-full w-1 transform -translate-x-1/2"
            style={{ backgroundColor: colors.primary }}
          />

          <div className="space-y-24">
            {timelineItems.map((item, index) => (
              <motion.div
                key={index}
                className="relative flex justify-center items-stretch"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-20%" }}
                variants={timelineVariant}
                custom={index}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {index > 0 && (
                  <div 
                    className="absolute left-1/2 w-1 h-10 -translate-x-1/2 top-0"
                    style={{ backgroundColor: colors.primary }}
                  />
                )}

                <div className="absolute top-1/2 left-1/2 w-[80%] h-0.5 transform -translate-y-1/2 -translate-x-1/2 opacity-40" style={{ backgroundColor: colors.textDark }} />

                <div 
                  className="relative w-full max-w-2xl border-2 rounded-xl p-8 shadow-lg transition-all duration-300"
                  style={{ 
                    backgroundColor: hoveredIndex === index ? colors.primary : colors.white,
                    borderColor: colors.primary
                  }}
                >
                  <div className="absolute -left-6 top-1/2 -translate-y-1/2">
                    <div className="relative h-5 w-5">
                      <div className="absolute inset-0 rounded-full shadow-md" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primary})` }} />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 
                      className="font-orbitron text-2xl transition-colors duration-300"
                      style={{ color: hoveredIndex === index ? colors.white : colors.textDark }}
                    >
                      {item.title}
                    </h3>
                    <p 
                      className="font-poppins text-lg leading-loose transition-colors duration-300"
                      style={{ color: hoveredIndex === index ? colors.white : colors.textDark }}
                    >
                      {item.content}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div 
          className="absolute inset-0 -z-10 opacity-10"
          style={{
            backgroundImage: `linear-gradient(to right, ${colors.textDark}10 1px, transparent 1px), linear-gradient(to bottom, ${colors.textDark}10 1px, transparent 1px)`,
            backgroundSize: "24px 24px"
          }}
        />
      </div>
    </section>
  );
};

// Timeline Animation Variant
const timelineVariant = {
  hidden: { opacity: 0, y: 50 },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: index * 0.4,
    },
  }),
};

// Timeline Data
const timelineItems = [
  {
    title: "Establishment & Vision",
    content:
      "Yegna Microfinance Institution S.C was founded with the vision to drive Ethiopia's economic development by offering tailored financial solutions to empower businesses and individuals.",
  },
  {
    title: "Financial Inclusion",
    content:
      "The institution focuses on bridging financial gaps by providing need-based services to micro, small, and medium enterprises (MSMEs), fostering economic growth and stability.",
  },
  {
    title: "Industry Impact",
    content:
      "With a dynamic team and strategic partnerships, Yegna Microfinance continues to expand, supporting economic inclusion and sustainable financial solutions.",
  },
  {
    title: "Commitment to Growth",
    content:
      "Yegna Microfinance Institution remains dedicated to financial empowerment, innovation, and sustainability, ensuring accessibility for all socioeconomic groups.",
  },
];

export default OurStory;